import React, { useRef, useState } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { UploadFileButton } from 'Modules/Reimbursements/FamilyMembers/UploadFileButton';
import {
  checkExtension,
  checkFileSize,
} from 'Modules/UniversalRequest/FileUpload/Utils';
import { isFileValid } from 'Utils/isFileValid';
import { ALLOWED_FILE_TYPES } from 'Config';

interface IUploadFileArea {
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  isTypeError: boolean;
  setIsTypeError: (value: boolean) => void;
}

export const UploadFileArea = ({
  setFile,
  isTypeError,
  setIsTypeError,
}: IUploadFileArea) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Feedback).uploadArea;
  const isDesktop = useIsDesktop();
  const fileInput = useRef<HTMLInputElement[]>([]);

  const handleFile = async (file: File) => {
    if (checkFileSize(file) && checkExtension(file)) {
      if (!(await isFileValid(file))) {
        setIsTypeError(true);
        return;
      }
      setIsTypeError(false);
      setFile(file);
    } else {
      setIsTypeError(true);
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file !== null) {
        handleFile(file);
        event.target.value = '';
      }
    }
  };

  const handleOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files[0];
    handleFile(droppedFiles);
    setIsDragActive(false);
  };

  const handleDragEnter = () => {
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const clickFileInput = (index: number) => {
    if (fileInput.current) {
      fileInput.current[index].click();
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: 'primary.shade_50',
        borderRadius: '16px',
        mt: '-24px',
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            border: isTypeError ? '1px solid #E12D2D' : '2px dashed #A020FF',
            padding: '0px 24px',
            background: isDragActive ? '#FBFCFF' : '#fff',
            borderRadius: '8px',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <Box
            onDragOver={(event) => event.preventDefault()}
            onDrop={(e) => handleOnDrop(e)}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              bgcolor: 'transparent',
              top: 0,
              left: 0,
              zIndex: isDragActive ? 1 : undefined,
            }}
          />
          {isDragActive ? (
            <Stack
              width={'551px'}
              height={'84px'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <Typography
                sx={{ pointerEvents: 'none', color: '#344266' }}
                variant='body6'
              >
                {dictionary.dragFileHere}
              </Typography>
            </Stack>
          ) : (
            <Stack direction={'row'} gap={'58px'} alignItems={'center'}>
              <Typography
                sx={{ pointerEvents: 'none', color: '#344266' }}
                variant='body3'
              >
                {dictionary.dragFileHere}
              </Typography>
              <Box width='111px'>
                <Divider
                  aria-hidden='true'
                  sx={{
                    margin: '30px 0',
                    pointerEvents: 'none',
                    '&::before, &::after': {
                      borderColor: 'primary.shade_200',
                    },
                  }}
                >
                  {dictionary.or}
                </Divider>
              </Box>
              <input
                ref={(e) => {
                  fileInput.current[0] = e!;
                }}
                type='file'
                accept={ALLOWED_FILE_TYPES}
                hidden
                onChange={(e) => onChangeHandler(e)}
                id={`upload-file`}
                aria-labelledby={isTypeError ? 'upload-file-error' : undefined}
              />
              <label htmlFor={`upload-file`} className='sr-only'>
                {`${dictionary.uploadFile}`}
              </label>
              <UploadFileButton
                variant='custom'
                customStyles={{
                  width: '158px',
                  bgcolor: 'purple2.shade_100',
                  border: 'none',
                }}
                clickHandler={() => clickFileInput(0)}
              >
                {dictionary.uploadFile}
              </UploadFileButton>
            </Stack>
          )}
        </Box>
      ) : (
        <Box>
          <input
            ref={(e) => {
              fileInput.current[0] = e!;
            }}
            type='file'
            accept={ALLOWED_FILE_TYPES}
            hidden
            onChange={(e) => onChangeHandler(e)}
            id={`upload-file`}
            aria-labelledby={isTypeError ? 'upload-file-error' : undefined}
          />
          <label htmlFor={`upload-file`} className='sr-only'>
            {`${dictionary.uploadFile}`}
          </label>

          <UploadFileButton clickHandler={() => clickFileInput(0)}>
            {dictionary.uploadFile}
          </UploadFileButton>
        </Box>
      )}
      {isTypeError && (
        <Typography
          color='#E12D2D'
          id='upload-file-error'
          role='alert'
          aria-live='assertive'
          variant='body6'
          pl='16px'
          maxWidth={'600px'}
          mt='6px'
        >
          {dictionary.typeError}
        </Typography>
      )}
    </Stack>
  );
};
